import { useEffect, useState } from 'react';
import { camelKeysShallow } from '@/services/_caseTransform';

/**
 * Branding + the sign-up offer, from the UNAUTHENTICATED `GET /api/public/portal-context`, so the
 * signed-out sign-in / sign-up pages can say what this is ("GizmoData Cloud" or "GizmoData Cloud at
 * Customer") and what a new account gets — truthfully, from the live offer state.
 */
export interface SignupOffer {
  code: string;
  trialCreditUsd: string | null;
  creditUsd: string | null;
  /** The offer only takes effect once a card is added (it has no card-free trial). */
  cardRequired: boolean;
}

export interface PortalContext {
  controlPlaneMode: 'public_cloud' | 'customer_hosted';
  productName: string;
  selfServeSignup: boolean;
  plansFromUsdPerMonth: string | null;
  signupOffer: SignupOffer | null;
  /** Custom DuckDB extension repositories are turned on for this deployment (a system-admin setting). */
  customExtensionRepositories?: boolean;
}

export const PUBLIC_CLOUD_NAME = 'GizmoData Cloud';
export const CLOUD_AT_CUSTOMER_NAME = 'GizmoData Cloud at Customer';
/** The hostname of GizmoData Cloud (the same check that loads the Google tag in index.html). */
const PUBLIC_CLOUD_HOST = 'app.gizmodata.com';

/** Best guess before the API answers: only app.gizmodata.com (and local dev) is GizmoData Cloud. */
export const guessProductName = (hostname: string = window.location.hostname): string =>
  hostname === PUBLIC_CLOUD_HOST || hostname === 'localhost' || hostname === '127.0.0.1'
    ? PUBLIC_CLOUD_NAME : CLOUD_AT_CUSTOMER_NAME;

const cache = new Map<string, Promise<PortalContext | null>>();

/** Fetch once per code per page load; null when the API doesn't have the endpoint (older API). */
export const getPortalContext = (code?: string): Promise<PortalContext | null> => {
  const key = code || '';
  let pending = cache.get(key);
  if (!pending) {
    const query = code ? `?code=${encodeURIComponent(code)}` : '';
    // A plain fetch: the endpoint needs no token, and the API client waits up to 3s for a Clerk
    // session before sending — exactly the signed-out pages this serves.
    const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://localhost:8000/api';
    pending = fetch(`${base}/public/portal-context${query}`, { headers: { Accept: 'application/json' } })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const body = camelKeysShallow<PortalContext & { signupOffer: unknown }>(await resp.json());
        return { ...body, signupOffer: body.signupOffer ? camelKeysShallow<SignupOffer>(body.signupOffer) : null };
      })
      .catch(() => {
        cache.delete(key);
        return null;
      });
    cache.set(key, pending);
  }
  return pending;
};

/** Forget the cached answer, e.g. after a system admin flips a setting the context reports. */
export const invalidatePortalContext = (): void => { cache.clear(); };

export function usePortalContext(code?: string): PortalContext | null {
  const [context, setContext] = useState<PortalContext | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getPortalContext(code).then((c) => { if (!cancelled) setContext(c); });
    return () => { cancelled = true; };
  }, [code]);
  return context;
}

/** "GizmoData Cloud" or "GizmoData Cloud at Customer" — the guess until the API answers. */
export function useProductName(): string {
  const context = usePortalContext();
  return context?.productName || guessProductName();
}

const usd = (value: string): string => {
  const n = Number(value);
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

/**
 * The sign-up page's offer line, literally true for the live offer state:
 * - a card-free trial credit → "Your $25 launch credit is applied automatically. No credit card needed."
 * - a credit granted at first Checkout → "Your $50 launch credit is applied when you subscribe."
 * - no redeemable offer → "Plans from $29/month — the monthly fee comes back as compute credit."
 */
export function signupOfferLine(context: PortalContext | null): string | null {
  if (!context || context.controlPlaneMode !== 'public_cloud') return null;
  const offer = context.signupOffer;
  if (offer?.trialCreditUsd && !offer.cardRequired) {
    return `Your ${usd(offer.trialCreditUsd)} launch credit is applied automatically. No credit card needed.`;
  }
  if (offer?.creditUsd) {
    return `Your ${usd(offer.creditUsd)} launch credit is applied when you subscribe.`;
  }
  if (context.plansFromUsdPerMonth) {
    return `Plans from ${usd(context.plansFromUsdPerMonth)}/month — the monthly fee comes back as compute credit.`;
  }
  return null;
}

/** "Start free" is only true when the carried code gives a card-free credit. */
export const isFreeStart = (context: PortalContext | null): boolean =>
  !!context?.signupOffer?.trialCreditUsd && !context.signupOffer.cardRequired;
